import { AdminTableContainer, AdminTableHeader, Button, EmptyState } from "@simplicity/ui";
import { useState } from "react";
import type { ConfigRevision } from "../../../configTypes";
import { clusterRevisions, type SaveEvent } from "./clusterRevisions";
import { TransformDiffSheet } from "./TransformDiffSheet";

interface TransformVersionHistoryListProps {
  revisions: ConfigRevision[];
  currentSqlTemplate: string;
  currentParams: Record<string, string>;
  onRestore: (sqlTemplate: string, params: Record<string, string>) => void;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function summarizeFields(saveEvent: SaveEvent): string {
  const fieldsByKey: Record<string, string> = {
    sqlTemplate: "SQL template",
    params: "Params",
  };
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const r of saveEvent.revisions) {
    if (seen.has(r.field)) continue;
    seen.add(r.field);
    labels.push(fieldsByKey[r.field] ?? r.field);
  }
  return labels.join(", ");
}

function SaveEventTableRow({ saveEvent, onOpen }: { saveEvent: SaveEvent; onOpen: () => void }) {
  return (
    <tr>
      {/* SIM-1689 followup: View column moved to first position + shortened
          from "View diff" → "View" so the row reads action-first when the
          column is narrow (right-col Version-History section). */}
      <td>
        <Button type="button" size="sm" variant="outline" onClick={onOpen}>
          View
        </Button>
      </td>
      <td className="col-muted">{formatTimestamp(saveEvent.createdAt)}</td>
      <td className="col-muted">{saveEvent.createdByEmail ?? "system"}</td>
      <td>{summarizeFields(saveEvent)}</td>
    </tr>
  );
}

export function TransformVersionHistoryList({
  revisions,
  currentSqlTemplate,
  currentParams,
  onRestore,
}: TransformVersionHistoryListProps) {
  const [activeEvent, setActiveEvent] = useState<SaveEvent | null>(null);
  const saveEvents = clusterRevisions(revisions);

  return (
    <>
      <AdminTableContainer aria-label="Version history">
        {/* SIM-1689 followup: removed internal "Version History" SectionHeading;
            parent inspector section now provides the heading. */}
        {saveEvents.length === 0 ? (
          <EmptyState
            title="No saved changes yet"
            description="Each time you save SQL or params, a snapshot is stored here. You can view diffs side-by-side and restore previous versions."
          />
        ) : (
          <div className="max-h-[220px] overflow-y-auto">
            <table className="admin-table">
              <AdminTableHeader>
                <tr>
                  <th></th>
                  <th>When</th>
                  <th>By</th>
                  <th>Changed</th>
                </tr>
              </AdminTableHeader>
              <tbody>
                {saveEvents.map((saveEvent) => (
                  <SaveEventTableRow
                    key={saveEvent.id}
                    saveEvent={saveEvent}
                    onOpen={() => setActiveEvent(saveEvent)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminTableContainer>
      <TransformDiffSheet
        saveEvent={activeEvent}
        currentSqlTemplate={currentSqlTemplate}
        currentParams={currentParams}
        open={activeEvent !== null}
        onOpenChange={(open) => {
          if (!open) setActiveEvent(null);
        }}
        onRestore={onRestore}
      />
    </>
  );
}
